import React, { useEffect, useState } from "react";
import { api, TERRITOIRES } from "../lib/api";
import { useApp } from "../context/AppContext";
import { ShieldAlert, ExternalLink } from "lucide-react";

export default function Risks() {
  const { territoire, setTerritoire } = useApp();
  const [profile, setProfile] = useState(null);

  useEffect(() => {
    api.get(`/risks/profile/${encodeURIComponent(territoire)}`).then((r) => setProfile(r.data)).catch(() => {});
  }, [territoire]);

  return (
    <div className="space-y-5" data-testid="risks-page">
      <h1 className="font-display text-3xl font-extrabold uppercase tracking-tight">Risques & Réglementation DROM</h1>

      <div className="flex gap-2" data-testid="risk-territories">
        {TERRITOIRES.map((t) => (
          <button key={t} data-testid={`risk-territory-${t.toLowerCase()}`} onClick={() => setTerritoire(t)}
            className={`px-5 py-3 rounded-xl font-bold border-2 touch-target ${territoire === t ? "bg-amber-500 text-slate-900 border-amber-500" : "bg-card border-border"}`}>
            {t}
          </button>
        ))}
      </div>

      {profile && (
        <>
          <div className="grid sm:grid-cols-3 gap-3">
            <div className="bg-card rounded-xl border-2 border-border p-5" data-testid="risk-seismic">
              <div className="text-xs font-bold uppercase text-muted-foreground">Zone de sismicité</div>
              <div className={`font-display text-5xl font-extrabold ${profile.zone_sismique >= 5 ? "text-red-600" : "text-emerald-600"}`}>{profile.zone_sismique}</div>
              <div className="text-xs mt-1 font-semibold">{profile.zone_sismique >= 5 ? "Zone 5 (forte) — Eurocode 8" : "Zone 1 (très faible)"}</div>
              {profile.microzonage_possible && <div className="text-xs mt-2 text-amber-600 font-bold">⚠ Microzonages communaux possibles — interroger Géorisques</div>}
            </div>
            <div className="bg-card rounded-xl border-2 border-border p-5" data-testid="risk-wind">
              <div className="text-xs font-bold uppercase text-muted-foreground">Vents cycloniques</div>
              <div className={`font-display text-5xl font-extrabold ${profile.vents_cycloniques ? "text-red-600" : "text-emerald-600"}`}>{profile.vents_cycloniques ? "OUI" : "NON"}</div>
              {profile.vents_cycloniques && <div className="text-xs mt-1 font-semibold">Exigences réglementaires depuis 2024 (Eurocode vent)</div>}
            </div>
            <div className="bg-card rounded-xl border-2 border-border p-5" data-testid="risk-tva">
              <div className="text-xs font-bold uppercase text-muted-foreground">TVA</div>
              {profile.tva?.applicable ? (
                <>
                  <div className="font-display text-5xl font-extrabold">{profile.tva.taux_normal}%</div>
                  <div className="text-xs mt-1 font-semibold">Taux réduit : {profile.tva.taux_reduit}%</div>
                </>
              ) : (
                <>
                  <div className="font-display text-4xl font-extrabold text-emerald-600">NON</div>
                  <div className="text-xs mt-1 font-semibold">Régime territorial sans TVA (textes applicables)</div>
                </>
              )}
              <div className="text-[10px] text-muted-foreground mt-2">Moteur paramétrable et versionné — à valider comptablement si interprétation.</div>
            </div>
          </div>

          <div className="bg-amber-50 dark:bg-amber-950 border-2 border-amber-400 rounded-xl p-4 text-sm font-semibold flex gap-2" data-testid="risk-disclaimer">
            <ShieldAlert className="h-5 w-5 shrink-0 text-amber-600" />
            <span>{profile.avertissement}</span>
          </div>

          <section className="bg-card rounded-xl border border-border p-5">
            <h2 className="font-display text-xl font-bold uppercase mb-3">Checklist climat tropical</h2>
            <ul className="grid sm:grid-cols-2 gap-1.5" data-testid="tropical-checklist">
              {profile.checklist_tropicale.map((c, i) => (
                <li key={i} className="text-sm flex gap-2 py-1"><span className="text-amber-600 font-bold">▸</span>{c}</li>
              ))}
            </ul>
          </section>

          <section className="bg-card rounded-xl border border-border overflow-x-auto">
            <h2 className="font-display text-xl font-bold uppercase p-5 pb-2">Sources réglementaires (versionnées)</h2>
            <table className="w-full text-sm min-w-[760px]" data-testid="regulatory-table">
              <thead className="bg-teca-900 text-white text-xs uppercase">
                <tr><th className="text-left px-3 py-2.5">Thème</th><th className="text-left px-3 py-2.5">Titre</th><th className="text-left px-3 py-2.5">Référence</th><th className="text-left px-3 py-2.5">Vérifié le</th><th className="text-left px-3 py-2.5">Source</th></tr>
              </thead>
              <tbody className="divide-y divide-border">
                {profile.regulatory_sources.map((s) => (
                  <tr key={s.id} data-testid={`reg-source-${s.id}`}>
                    <td className="px-3 py-2.5 font-bold">{s.theme}</td>
                    <td className="px-3 py-2.5">{s.titre}</td>
                    <td className="px-3 py-2.5 text-xs">{s.reference_texte}</td>
                    <td className="px-3 py-2.5 text-xs font-mono-num">{s.date_verification}</td>
                    <td className="px-3 py-2.5">
                      <a href={s.url} target="_blank" rel="noreferrer" className="text-sky-600 font-bold text-xs inline-flex items-center gap-1" data-testid={`reg-link-${s.id}`}>
                        {s.source} <ExternalLink className="h-3 w-3" />
                      </a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        </>
      )}
    </div>
  );
}
